import React from 'react';
import {
  ArrowRight,
  ClipboardCheck,
  Code2,
  FileText,
  GitBranch,
  Globe2,
  Layers,
  Phone,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  CapabilityGrid,
  CONTACT_PATH,
  DEMO_PATH,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const HERO_STEPS = [
  { label: 'Leadership', note: 'The first hires who set the centre' },
  { label: 'Core team', note: 'Engineering and business roles' },
  { label: 'Functional expansion', note: 'More functions on the same plan' },
  { label: 'High-volume hiring', note: 'A larger applicant set' },
  { label: 'Continuous talent acquisition', note: 'The same path, used again' },
];

const LIFECYCLE = [
  { title: 'Leadership hiring', detail: 'Start with the people who set how the India team hires.', icon: UserCheck },
  { title: 'Core engineering / business team', detail: 'Screen and assess the first working team against each role.', icon: Code2 },
  { title: 'Functional expansion', detail: 'Add functions without inventing a new process for each one.', icon: GitBranch },
  { title: 'High-volume hiring', detail: 'Use the same screening and interview path when applicant volume grows.', icon: Layers },
  { title: 'Continuous talent acquisition', detail: 'Keep the hiring path in place as the centre keeps hiring.', icon: Globe2 },
];

const ENGINE = [
  { title: 'Hiring plan', detail: 'The roles and criteria for this stage of the centre.', icon: FileText },
  { title: 'Sourcing', detail: 'Find people who fit those roles.', icon: Users },
  { title: 'Screening', detail: 'Read resumes and early answers before a recruiter spends time.', icon: ClipboardCheck },
  { title: 'Assessment', detail: 'Use the assessment that matches the job.', icon: ClipboardCheck },
  { title: 'Interview', detail: 'Keep a structured interview the team can review.', icon: Video },
  { title: 'Shortlist', detail: 'A person compares the records.', icon: UserCheck },
  { title: 'Ongoing hiring support', detail: 'The same path stays available as hiring continues.', icon: Layers },
];

const IDEAL_FOR = [
  'New India GCCs',
  'Existing GCC expansion',
  'Engineering centres',
  'Shared services',
  'Global product teams',
];

const STAGES = [
  { title: 'Leadership hiring', detail: 'Targeted sourcing and a structured evaluation.' },
  { title: 'Core team', detail: 'Role-specific screening and assessments.' },
  { title: 'Functional expansion', detail: 'More than one role on the same workflow.' },
  { title: 'High-volume hiring', detail: 'Screening and structured interviews across a larger set.' },
  { title: 'Continuous talent acquisition', detail: 'The same hiring path, ready to run again.' },
];

const AI_WORK = [
  'Sourcing support',
  'Screening',
  'Assessment',
  'Repetitive interviews',
  'Candidate evidence',
];

const HUMAN_WORK = [
  'Leadership review',
  'Recruiter review',
  'Hiring manager judgment',
  'Final decision',
];

const PRODUCTS = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Who enters the pipeline, and how the resume fits the role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'AI Call Screening',
    helps: 'How a candidate answers role questions on a structured call.',
    href: '/hirewithus/ai-call-screening',
    icon: Phone,
  },
  {
    title: 'Screening Assessment',
    helps: 'How the resume scores against the job description.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, communication, and role-based skills.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Coding Assessment',
    helps: 'Whether technical candidates can do the work.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A structured AI interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

export default function GccHiring() {
  useSEO({
    title: 'GCC Hiring in India | AI Recruitment | SearchJob.ai',
    description:
      'Build and scale India GCC teams with AI-powered sourcing, screening, assessments, interviews and recruitment.',
    canonical: 'https://searchjob.ai/solutions/gcc-hiring',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <Lifecycle />
        </ScrollReveal>
        <ScrollReveal>
          <Engine />
        </ScrollReveal>
        <ScrollReveal>
          <IdealFor />
        </ScrollReveal>
        <ScrollReveal>
          <StageSupport />
        </ScrollReveal>
        <ScrollReveal>
          <Products />
        </ScrollReveal>
        <ScrollReveal>
          <HumanLoop />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:items-stretch lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Globe2 className="h-3.5 w-3.5" aria-hidden="true" />
            GCC hiring
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Building a GCC in India?
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            Build the team that makes it work. From the first leadership hire to hundreds of engineers and business professionals, SearchJob.ai helps companies establish and scale India teams.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Build Your India Hiring Plan
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href={CONTACT_PATH}>Talk to a GCC Hiring Expert</a>
            </Button>
          </div>
        </div>
        <GrowthPath />
      </div>
    </section>
  );
}

function GrowthPath() {
  return (
    <div className="relative flex flex-col" aria-hidden="true">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">How a GCC team grows</p>
      <ol className="rounded-3xl border border-primary bg-primary/5 px-5 py-2 lg:flex lg:flex-1 lg:flex-col">
        {HERO_STEPS.map((step) => (
          <li key={step.label} className="flex items-start gap-3 border-b border-primary/15 py-3 last:border-b-0 lg:flex-1">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
              <span>
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="block text-sm text-muted-foreground">{step.note}</span>
              </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Lifecycle() {
  return (
    <Section id="gcc-lifecycle" muted>
      <SectionIntro
        eyebrow="GCC hiring lifecycle"
        title="Five stages. One hiring plan that can grow."
        copy="Select a phase. The centre can move from the first leaders to ongoing hiring without starting over."
      />
      <IndustryWorkflow steps={LIFECYCLE} />
    </Section>
  );
}

function Engine() {
  return (
    <Section>
      <SectionIntro
        eyebrow="India hiring layer"
        title="Your AI talent acquisition engine for India."
        copy="Use SearchJob as the recruitment operating layer while the GCC grows. The stages change. The hiring path can stay."
      />
      <IndustryWorkflow steps={ENGINE} />
    </Section>
  );
}

function IdealFor() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Ideal for"
        title="Teams building or growing an India centre."
        copy="These are the situations this page is written for."
      />
      <ul className="flex flex-wrap gap-3">
        {IDEAL_FOR.map((item) => (
          <li key={item}>
            <span className="inline-flex min-h-11 items-center rounded-full border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-foreground">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function StageSupport() {
  return (
    <Section>
      <SectionIntro
        eyebrow="Each stage"
        title="How SearchJob supports the lifecycle."
        copy="The same products cover each stage. Nothing here is a GCC-only feature."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {STAGES.map((item, index) => (
          <article key={item.title} className="rounded-2xl border border-primary bg-primary/5 p-5">
            <span className="mb-3 flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
              {index + 1}
            </span>
            <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
          </article>
        ))}
      </div>
    </Section>
  );
}

function Products() {
  return (
    <Section muted>
      <SectionIntro
        eyebrow="Existing capabilities"
        title="Products already on SearchJob.ai."
        copy="These are the tools behind the GCC hiring path."
      />
      <CapabilityGrid items={PRODUCTS} />
    </Section>
  );
}

function HumanLoop() {
  return (
    <Section>
      <SectionIntro
        eyebrow="AI and human control"
        title="AI handles repetitive recruitment work. Humans make hiring decisions."
        copy="SearchJob.ai collects evidence. It does not make the hiring decision."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <WorkList title="AI handles" items={AI_WORK} />
        <WorkList title="Your team controls" items={HUMAN_WORK} />
      </div>
    </Section>
  );
}

function WorkList({ title, items }) {
  return (
    <article className="rounded-2xl border border-primary bg-primary/5 p-5">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Build Your India Hiring Plan"
      copy="Use SearchJob.ai to support leadership hiring, core team building, functional expansion, and continued talent acquisition as your GCC grows."
      primaryLabel="Build Your India Hiring Plan"
      primaryHref={DEMO_PATH}
      secondaryLabel="Talk to a GCC Hiring Expert"
      secondaryHref={CONTACT_PATH}
    />
  );
}
